<template>
  <v-menu
    content-class="popover-menu-vuetify popover-calendar"
    offset-y
    v-bind:close-on-content-click="false"
    v-bind:value="active"
    v-on:update:return-value="onDialogClose"
  >
    <template v-slot:activator="{ on }">
      <slot
        name="trigger"
        v-bind:listener-object="on" />
    </template>
    <date-range-picker
      :key="datePickerKey"
      class="date-picker"
      opens="inline"
      v-bind:locale-data="this.$options.localeData"
      v-bind:ranges="false"
      v-bind:single-date-picker="range ? 'range' : 'single'"
      v-bind:auto-apply="true"
      v-bind:date-format="dateFormat"
      v-model="date"
      @update="handleDateUpdate"
      @toggle="checkMonthChange"
      @change-month="onChangeMonth"
    />
  </v-menu>
</template>
<script>
import DateRangePicker from '@/components/DateRangePicker.vue';

const localeData = {
  daysOfWeek: ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ'],
  monthNames: ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'],
  firstDay: 1,
};

const NAME_EVENT_INPUT = 'input';
const NAME_EVENT_CHANGE_MONTH = 'change-month';
const NAME_EVENT_CLOSE = 'close';
/**
 * @description Компонент диалога выбора даты.
 * @type {Vue}
 */
export default {
  localeData,
  components: {
    DateRangePicker,
  },
  computed: {
    /**
     * @description Формат дат для DateRangePicker.
     * @returns {(function(*, *): (*))|*}
     */
    dateFormat() {
      return (classes, date) => {
        if (this.allowedDates === null) return classes;
        const dateString = date.toISOString().split('T')[0];
        return { ...classes, disabled: !this.allowedDates(dateString) };
      };
    },
  },
  created() {
    this.preselectDate();
  },
  data() {
    // eslint-disable-next-line no-nested-ternary
    const initialDate = this.range && this.value.length
      ? this.value[0]
      : this.value instanceof Date ? this.value : null;
    return {
      active: false,
      date: { startDate: null, endDate: null },
      datePickerKey: 1,
      currentMonth: initialDate ? initialDate.getMonth() : new Date().getMonth(),
    };
  },
  name: 'DatePickerDialog',
  methods: {
    preselectDate() {
      let startDate = null;
      let endDate = null;
      if (Array.isArray(this.value)) {
        startDate = this.value[0] ?? null;
        endDate = this.value[1] ?? null;
      } else {
        startDate = this.value;
        endDate = this.value;
      }
      this.date = { startDate, endDate };
    },
    /**
     * @description Обновить текущий месяц.
     * @returns {void}
     */
    updateCurrentMonth() {
      const baseDate = this.date.startDate;
      if (baseDate) {
        this.currentMonth = baseDate.getMonth();
      }
    },
    /**
     * @description Прослушивание обновления даты.
     * @returns {void}
     */
    handleDateUpdate() {
      if (this.range) {
        const range = [this.date.startDate, this.date.endDate].sort((a, b) => a - b);
        this.closeDialog();
        this.$emit(NAME_EVENT_INPUT, range);
      } else {
        this.datePickerKey += 1;
        this.closeDialog();
        this.$emit(NAME_EVENT_INPUT, this.date.startDate);
      }
    },
    /**
     * @description Проверить изменение месяца.
     * @returns {void}
     */
    checkMonthChange() {
      const newMonth = this.date.startDate?.getMonth();
      if (newMonth !== undefined && newMonth !== this.currentMonth) {
        this.currentMonth = newMonth;
        this.datePickerKey += 1;
        this.onChangeMonth(this.date.startDate);
      }
    },
    /**
     * @description Обработать изменение месяца.
     * @param {Date} date
     * @returns {void}
     */
    onChangeMonth(date) {
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      this.$emit(NAME_EVENT_CHANGE_MONTH, `${year}-${month}`);
    },
    /**
     * @description Закрыть диалоговое окно.
     * @returns {void}
     */
    closeDialog() {
      this.active = true;
      this.$nextTick(() => {
        this.active = false;
      });
    },
    /**
     * @description Обработать событие закрытия диалогового окна.
     * @returns {void}
     */
    onDialogClose() {
      this.$emit(NAME_EVENT_CLOSE);
    },
  },
  props: {
    allowedDates: { type: Function, default: null },
    range: { type: Boolean, default: false },
    value: { type: [Date, Array], default: () => null },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        if (this.range) {
          this.date = Array.isArray(newVal)
            ? { startDate: newVal[0], endDate: newVal[1] }
            : { startDate: null, endDate: null };
        } else {
          this.date = { startDate: newVal, endDate: newVal };
        }
        this.updateCurrentMonth();
      },
    },
  },
};
</script>
<style lang="scss">
@import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

.popover-menu-vuetify.popover-calendar {
  background-color: #ffffff;
  .date-picker {
    width: 250px;
    margin: 0;
    border: none;
  }
  .calendars {
    padding-block: 4px;
  }
}

.date-picker {
  .calendars {
    justify-content: center;
  }
  .v-picker__body {
    width: 246px !important;
  }
  .v-date-picker-table {
    height: auto;
    padding: 0;
    th {
      font-weight: 400;
      font-size: 10px;
      &:nth-child(6n),
      &:nth-child(7n) {
        color: #EF474C;
      }
    }
  }
  .v-date-picker-header {
    background: #EAEFF3;
  }
  .theme--light.v-date-picker-header .v-date-picker-header__value:not(.v-date-picker-header__value--disabled) button:not(:hover):not(:focus) {
    color: #0489E0;
  }
  .theme--light.v-date-picker-header .v-date-picker-header__value button:hover {
    color: #192A3E;
  }
  .theme--light.v-btn.v-btn--disabled, .theme--light.v-btn.v-btn--disabled .v-btn__loading, .theme--light.v-btn.v-btn--disabled .v-icon {
    color: #90A0B7 !important;
  }
  .v-date-picker-header__value button {
    padding: 5px 0;
    font-weight: 600;
    font-size: 14px;
    line-height: 16px;
  }
  .v-btn--icon.v-size--default {
    height: 20px;
    width: 20px;
  }
  .theme--light.v-btn.v-btn--icon {
    color: transparent;
  }
  .theme--light.v-btn:hover:before {
    opacity: 0;
  }
  .mdi-chevron-left::before {
    content: '';
    background-image: url("@/assets/img/arrow/arrow-prev-grey.svg");
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #90A0B7;
    background-position: center;
    background-repeat: no-repeat;
  }
  .mdi-chevron-left:hover::before {
    background-image: url("@/assets/img/arrow/arrow-prev-blue.svg");
    border: 1px solid #0489E0;
  }
  .mdi-chevron-right::before {
    content: '';
    background-image: url("@/assets/img/arrow/arrow-next-grey.svg");
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #90A0B7;
    background-position: center;
    background-repeat: no-repeat;
  }
  .mdi-chevron-right:hover::before {
    background-image: url("@/assets/img/arrow/arrow-next-blue.svg");
    border: 1px solid #0489E0;
  }
  .v-btn--rounded {
    border-radius: 2px;
  }
  .v-date-picker-table--date .v-btn {
    height: 18px;
    width: 18px;
    font-weight: 400;
  }
  .theme--light.v-btn--active:before, .theme--light.v-btn--active:hover:before {
    opacity: 0;
  }
}
@media screen and (max-width: 480px) {
  .date-picker {
    width: 100%;
    .v-picker__body {
      width: 100% !important;
    }
  }
}

// Скрывает встроенный инпут пикера.
.reportrange-text {
  display: none;
}
.daterangepicker {
  margin-top: 0;
  border: none;
  .drp-calendar {
    width: auto;
    max-width: inherit;
  }
  .drp-calendar.left {
    padding: 0;
    width: 246px;
  }
  .calendar-table .next span,
  .calendar-table .prev span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-inline: auto;
    border-radius: 50%;
    border: 1px solid #90A0B7;
    background-position: center;
    background-repeat: no-repeat;
    transform: rotate(0);
  }
  .calendar-table .next span {
    background-image: url("@/assets/img/arrow/arrow-next-grey.svg");
  }
  .calendar-table .prev span {
    background-image: url("@/assets/img/arrow/arrow-prev-grey.svg");
  }
  th.month {
    color: #357ebd;
    font-size: 14px;

  }
}
@media (min-width: 564px) {
  .daterangepicker.ltr .drp-calendar.left .calendar-table {
    padding: 0;

  }
}
</style>
